import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Lock } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ApiRequestError } from '@/lib/apiClient';
import { PasswordStrength } from '@/features/auth/components/PasswordStrength';
import { setPasswordSchema, type SetPasswordValues } from '@/features/auth/schemas/password.schema';
import { institutesApi } from '../api/institutes.api';
import { applyFieldErrors } from '@/lib/forms';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface SetAdminPasswordDialogProps {
  instituteUuid: string;
  /** Shown so it is obvious whose password is about to change. */
  adminEmail: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Sets an institute administrator's password on their behalf.
 *
 * Support of last resort, for an administrator who cannot receive the emailed
 * link. The screen says plainly what it costs: we end up knowing a customer's
 * password until they sign in and are made to replace it — which is why
 * resending the link is offered first, right above this.
 */
export function SetAdminPasswordDialog({
  instituteUuid,
  adminEmail,
  isOpen,
  onClose,
}: SetAdminPasswordDialogProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const [doneFor, setDoneFor] = useState<string | null>(null);

  const form = useForm<SetPasswordValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);
  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  // Never leave a typed password sitting in a closed dialog.
  useEffect(() => {
    if (isOpen) {
      setFormError(null);
      setDoneFor(null);
      reset({ newPassword: '', confirmPassword: '' });
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const admin = await institutesApi.setAdminPassword(instituteUuid, values);
      setDoneFor(admin.email);
      reset({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(setError, error);
        setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Set the administrator's password"
      description={doneFor ? undefined : `This replaces the password for ${adminEmail}.`}
      size="sm"
      footer={
        doneFor ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={onSubmit} isLoading={isSubmitting}>
              {isSubmitting ? 'Setting…' : 'Set password'}
            </Button>
          </>
        )
      }
    >
      {doneFor ? (
        <Alert tone="success" title="Password set">
          Tell {doneFor} the password you just chose. Every device they were signed in on has been
          signed out, and they will be asked to choose their own password as soon as they sign in —
          after which the one you set stops working.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Alert tone="warning" title="Use this only when the link cannot reach them">
            You will know this password until they replace it, and anything done with the account in
            the meantime is recorded against them, not you. Resending the sign-in link is the safer
            option wherever it is possible.
          </Alert>

          {formError && (
            <Alert tone="critical" title="Could not set the password">
              {formError}
            </Alert>
          )}

          <Input
            label="New password"
            placeholder="Enter new password"
            autoComplete="new-password"
            isPassword
            leftIcon={<KeyRound className="h-4 w-4" />}
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />
          <Input
            label="Confirm password"
            placeholder="Re-enter new password"
            autoComplete="new-password"
            isPassword
            leftIcon={<Lock className="h-4 w-4" />}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
          {/* The same checklist every other password screen shows, read from
              the rules the server enforces — so the rules are seen before a
              refused attempt, not after. */}
          <PasswordStrength value={newPassword} confirmation={confirmPassword} />
        </form>
      )}
    </Modal>
  );
}
